/**
 * Access Level Enum for Role Access Configuration
 *
 * Defines the 4 access levels that can be assigned to permission groups per role.
 * Used by the AccessLevelMatrix component and BusinessRolePolicy model.
 *
 * @see Issue: Role Access Configuration
 */

/**
 * Access levels for permission group configuration.
 *
 * Each level is cumulative — EDIT includes VIEW permissions, ADMIN includes EDIT + VIEW.
 */
export enum AccessLevel {
  /** No access to this permission group */
  NONE = 'none',
  /** Read-only access */
  VIEW = 'view',
  /** Read + create + update access */
  EDIT = 'edit',
  /** Full access including delete, manage, approve */
  ADMIN = 'admin',
}

/**
 * Ordered array of access levels from least to most permissive.
 * Used for comparison and slider/dropdown UIs.
 */
export const ACCESS_LEVEL_ORDER: readonly AccessLevel[] = [
  AccessLevel.NONE,
  AccessLevel.VIEW,
  AccessLevel.EDIT,
  AccessLevel.ADMIN,
] as const;

/**
 * Display labels for access levels (for non-i18n contexts like logging)
 */
export const ACCESS_LEVEL_LABELS: Record<AccessLevel, string> = {
  [AccessLevel.NONE]: 'No Access',
  [AccessLevel.VIEW]: 'View',
  [AccessLevel.EDIT]: 'Edit',
  [AccessLevel.ADMIN]: 'Admin',
};

/**
 * Check if one access level is at least as permissive as another
 */
export function isAccessLevelAtLeast(level: AccessLevel, minimum: AccessLevel): boolean {
  return ACCESS_LEVEL_ORDER.indexOf(level) >= ACCESS_LEVEL_ORDER.indexOf(minimum);
}
